import {HTMLAttributes, useCallback, useEffect, useState} from 'react';
import {t} from '../../../../common/getTranslations';
import {requestContext} from '../../../../common/requestContext';
import useTypedDispatch from '../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../common/useTypedSelector';
import {ContentHomeProductListTab} from '../../../../design-system/organisms/ContentHomeProductListTab/ContentHomeProductListTab';
import {ContentHomeTabWrapper} from '../../../../design-system/organisms/ContentHomeTabWrapper/ContentHomeTabWrapper';
import {NoItem} from '../../../../design-system/organisms/NoItem/NoItem';
import {ApiErrorBoundary} from '../../../../errors/ApiErrorBoundary.class';
import {
  ContentHomeProductListQuery,
  Maybe,
  SaleState,
  SortOption,
} from '../../../../graphql/generated/types';
import {ONLY_DATA_CONFIG} from '../../../../reactQuery/constant';
import {ReactQueryFetcher} from '../../../../reactQuery/ReactQueryFetcher';
import {useLogin} from '../../../../user/useLogin.hook';
import {
  useContentHomeOverviewQuery,
  useContentHomeProductListQuery,
} from '../../../../wrapper/contentWrapper';
import {ContentOnlyLoggedInOrSaleCheckerHeadless} from '../headless/ContentOnlyLoggedInOrSaleCheckerHeadless';
import {useContentHomeLastProductList} from '../hook/useContentHomeLastProductList';
import {useProductListSortOptDialog} from '../hook/useProductListSortOptDialog';
import {
  setCanRead,
  setIsCheckedBoughtOnly,
  setProductListSortOption,
  updateProductEdgeListMap,
} from '../state/contentHomeSlice';
import {ContentHomeTabInfoContainer} from './ContentHomeTabInfoContainer';
import {ContentProductListMobileConnectionContainer} from './ContentProductListMobileConnectionContainer';

export interface ContentHomeProductTabMobileContainerProps
  extends HTMLAttributes<HTMLDivElement> {
  seriesId: number;
}

export function ContentHomeProductTabMobileContainer({
  seriesId,
  ...props
}: ContentHomeProductTabMobileContainerProps) {
  const overview = useContentHomeOverviewQuery({seriesId}, ONLY_DATA_CONFIG)
    .data?.contentHomeOverview;
  const {selectedSortOption, isCheckedBoughtOnly} = useTypedSelector(state => ({
    selectedSortOption: state.contentHome.productListSortOption,
    isCheckedBoughtOnly: state.contentHome.isCheckedBoughtOnly,
  }));
  const {isLogin} = useLogin();
  const {isMobile} = requestContext.requestInfo.display;
  const dispatch = useTypedDispatch();

  const [sortOptList, setSortOptList] =
    useState<Maybe<Array<Maybe<SortOption>>>>(undefined);

  const onToggleBoughtOnly = useCallback(() => {
    dispatch(setIsCheckedBoughtOnly(!isCheckedBoughtOnly));
  }, [dispatch, isCheckedBoughtOnly]);
  const onSelectSortOption = useCallback(
    (sortOpt: SortOption) => {
      dispatch(setProductListSortOption({sortOption: sortOpt}));
    },
    [dispatch]
  );

  const {showDialog, productListSortOptDialog} = useProductListSortOptDialog({
    isMobile,
    sortOptList,
    selectedSortOpt: selectedSortOption,
    onSelectSortOption,
    isProductList: true,
  });
  const {visit} = useContentHomeLastProductList(seriesId);
  useEffect(() => {
    visit();
  }, [visit]);

  const onSuccess = useCallback(
    ({contentHomeProductList}: ContentHomeProductListQuery) => {
      if (!contentHomeProductList) return;
      // 모바일은 INIT 값을 통해 sort 값을 업데이트 해줍니다.
      setSortOptList(contentHomeProductList.sortOptionList);
      dispatch(
        setProductListSortOption({
          sortOption: contentHomeProductList.selectedSortOption,
        })
      );
      // init map
      dispatch(
        updateProductEdgeListMap({
          seriesId,
          edgeList: contentHomeProductList.edges,
        })
      );
      if ((contentHomeProductList?.totalCount || 0) > 0) {
        dispatch(setCanRead({seriesId, canRead: true}));
      }
    },
    [dispatch, seriesId]
  );

  useEffect(() => {
    return () => {
      dispatch(setProductListSortOption({sortOption: undefined}));
      dispatch(setIsCheckedBoughtOnly(false));
    };
  }, [dispatch]);

  return (
    <ContentHomeTabWrapper
      isMobile={true}
      displayAd={overview?.displayAd}
      saleState={overview?.content?.saleState}
      {...props}
    >
      <div className="flex flex-1 flex-col pt-8pxr">
        <ContentHomeTabInfoContainer seriesId={seriesId} className="mx-11pxr" />
        <ContentOnlyLoggedInOrSaleCheckerHeadless seriesId={seriesId}>
          <ApiErrorBoundary>
            <ReactQueryFetcher
              reactQuery={useContentHomeProductListQuery}
              variables={{
                seriesId,
                boughtOnly: isCheckedBoughtOnly,
                sortType: selectedSortOption?.param,
              }}
              onSuccess={onSuccess}
            >
              {({contentHomeProductList}) => (
                <ContentHomeProductListTab
                  mode={'totalList'}
                  boughtOnly={isCheckedBoughtOnly}
                  onClickBoughtOnly={onToggleBoughtOnly}
                  totalCount={contentHomeProductList?.totalCount}
                  selectedSortOption={
                    contentHomeProductList?.selectedSortOption
                  }
                  sortOptionList={contentHomeProductList?.sortOptionList}
                  onClickSortOption={showDialog}
                  hideBoughtOnlyOption={
                    overview?.content?.saleState !== SaleState.Ing || !isLogin
                  }
                  isMobile={requestContext.requestInfo.display.isMobile}
                  isWebView={requestContext.requestInfo.display.isWebView}
                >
                  <>
                    {!!contentHomeProductList?.totalCount && (
                      <ContentProductListMobileConnectionContainer
                        seriesId={seriesId}
                        boughtOnly={isCheckedBoughtOnly}
                        sortOption={selectedSortOption}
                        totalCount={contentHomeProductList?.totalCount}
                        className="mb-28pxr mt-8pxr"
                      />
                    )}
                    {!contentHomeProductList?.totalCount &&
                      isCheckedBoughtOnly && (
                        <NoItem isMobile={true}>
                          {t('contenthome_ep_tab_purchased_ep_blank')}
                        </NoItem>
                      )}
                  </>
                </ContentHomeProductListTab>
              )}
            </ReactQueryFetcher>
          </ApiErrorBoundary>
        </ContentOnlyLoggedInOrSaleCheckerHeadless>
        {productListSortOptDialog}
      </div>
    </ContentHomeTabWrapper>
  );
}
